import AppIcon from '../components/AppIcon';
import React, { useState } from 'react';

const TABS = [
  { key: 'sent', label: 'Sent', icon: 'send' },
  { key: 'received', label: 'Received', icon: 'inbox' },
];

const STATUS_CONFIG = {
  PENDING: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-400' },
  ACCEPTED: { label: 'Accepted', color: 'bg-green-100 text-green-800', dot: 'bg-green-400' },
  REJECTED: { label: 'Rejected', color: 'bg-red-100 text-red-800', dot: 'bg-red-400' },
  EXPIRED: { label: 'Expired', color: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
};

const DEMO_SENT = [
  { id: '1', recipient: 'cousin@example.com', method: 'EMAIL', status: 'PENDING', sentDate: '2024-04-20', respondedDate: null, permissionLevel: 'VIEWER', relationshipHint: 'We are cousins through my mother\'s side' },
  { id: '2', recipient: '+1-555-123-4567', method: 'PHONE', status: 'ACCEPTED', sentDate: '2024-04-15', respondedDate: '2024-04-16', permissionLevel: 'CONTRIBUTOR', relationshipHint: 'My father is your uncle' },
  { id: '3', recipient: 'aunt@example.com', method: 'EMAIL', status: 'EXPIRED', sentDate: '2024-03-01', respondedDate: null, permissionLevel: 'VIEWER', relationshipHint: '' },
];

const DEMO_RECEIVED = [
  { id: '4', sender: 'Uncle Tom', method: 'EMAIL', status: 'PENDING', sentDate: '2024-04-22', permissionLevel: 'CONTRIBUTOR', relationshipHint: 'Your cousin from Sacramento' },
  { id: '5', sender: 'Aunt Mai', method: 'EMAIL', status: 'ACCEPTED', sentDate: '2024-03-10', respondedDate: '2024-03-11', permissionLevel: 'VIEWER', relationshipHint: 'Family friend from church' },
];

function InvitationsPage() {
  const [activeTab, setActiveTab] = useState('sent');
  const [filterStatus, setFilterStatus] = useState('ALL');

  const sent = DEMO_SENT;
  const received = DEMO_RECEIVED;

  const filteredSent = filterStatus === 'ALL'
    ? sent
    : sent.filter((i) => i.status === filterStatus);

  const filteredReceived = filterStatus === 'ALL'
    ? received
    : received.filter((i) => i.status === filterStatus);

  return (
    <div className="">

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-1 -mb-px">
            {TABS.map((tab) => {
              const count = tab.key === 'sent' ? sent.length : received.length;
              return (
                <button
                  key={tab.key}
                  onClick={() => { setActiveTab(tab.key); setFilterStatus('ALL'); }}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <AppIcon name={tab.icon} size={16} />
                  {tab.label}
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                    activeTab === tab.key ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
                  }`}>{count}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Status Filter */}
        <div className="flex flex-wrap gap-2 mb-4">
          {['ALL', 'PENDING', 'ACCEPTED', 'REJECTED', 'EXPIRED'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filterStatus === status
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {status === 'ALL' ? 'All' : STATUS_CONFIG[status]?.label || status}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'sent' ? (
          <SentInvitations invitations={filteredSent} />
        ) : (
          <ReceivedInvitations invitations={filteredReceived} />
        )}
      </main>
    </div>
  );
}

function SentInvitations({ invitations }) {
  if (invitations.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
        <AppIcon name="send" size={60} />
        <h3 className="mt-4 text-xl font-semibold text-gray-900">No sent invitations</h3>
        <p className="mt-2 text-gray-500">Invite family members to join your family tree.</p>
        <a href="/invitations/new" className="inline-block mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
          + Invite Family
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {invitations.map((inv) => {
        const status = STATUS_CONFIG[inv.status];
        return (
          <div key={inv.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-all">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 truncate">{inv.recipient}</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                    {status.label}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                  <span>{inv.method === 'EMAIL' ? <> <AppIcon name="email" size={14} /> Email</> : <> <AppIcon name="phone" size={14} /> SMS</>}</span>
                  <span>Sent {inv.sentDate}</span>
                  {inv.respondedDate && <span>Responded {inv.respondedDate}</span>}
                  <span className="capitalize">{inv.permissionLevel.toLowerCase()}</span>
                </div>
                {inv.relationshipHint && (
                  <p className="mt-1 text-xs text-gray-400 italic">"{inv.relationshipHint}"</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {inv.status === 'PENDING' && (
                  <>
                    <button className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors">
                      Resend
                    </button>
                    <button className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">
                      Cancel
                    </button>
                  </>
                )}
                {inv.status === 'EXPIRED' && (
                  <button className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                    Resend
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReceivedInvitations({ invitations }) {
  if (invitations.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
        <AppIcon name="inbox" size={60} />
        <h3 className="mt-4 text-xl font-semibold text-gray-900">No received invitations</h3>
        <p className="mt-2 text-gray-500">When someone invites you, it will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {invitations.map((inv) => {
        const status = STATUS_CONFIG[inv.status];
        return (
          <div key={inv.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-all">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">From {inv.sender}</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                    {status.label}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                  <span>{inv.method === 'EMAIL' ? <> <AppIcon name="email" size={14} /> Email</> : <> <AppIcon name="phone" size={14} /> SMS</>}</span>
                  <span>Received {inv.sentDate}</span>
                  <span className="capitalize">{inv.permissionLevel.toLowerCase()} access</span>
                </div>
                {inv.relationshipHint && (
                  <p className="mt-1 text-xs text-gray-400 italic">"{inv.relationshipHint}"</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {inv.status === 'PENDING' && (
                  <>
                    <button className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors">
                      Accept
                    </button>
                    <button className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">
                      Reject
                    </button>
                  </>
                )}
                {inv.status === 'ACCEPTED' && (
                  <span className="text-xs text-green-600 font-medium">Accepted</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default InvitationsPage;